import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Animated,
  Platform,
  TextInput,
  ActivityIndicator,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Linking,
} from 'react-native';
import { useRouter } from 'expo-router';
import { X, CircleCheck as CheckCircle2 } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { Course } from '@/types/chat';
import { supabase } from '@/utils/supabase';
import { LEAD_GATE_COPY } from '@/constants/leadGateCopy';
import {
  trackPopupView,
  getCurrentUserId,
  getOrCreateSessionId,
} from '@/utils/courseAnalyticsService';

interface CourseExtended {
  id?: string | number;
  name?: string;
  title?: string | null;
  original_title?: string | null;
  mentor?: string;
  type?: string;
  duration?: string;
  price?: string;
  learningType?: string;
  audience?: string | null;
  outcomes?: string | null;
  uniqueness?: string | null;
  target_skills?: string | null;
  targetSkills?: string | null;
  sourceUrls?: string;
  sourceUrl?: string;
  url?: string;
  marketing_title?: string | null;
  summary?: string | null;
  description?: string;
  imageUrl?: string;
  landingUrl?: string;
  language?: string | null;
  course_id?: string;
  relevant?: string | number | null;
  fit_line?: string | null;
  unique_line?: string | null;
  outcome_line?: string | null;
  deliverables?: string | null;
  source_urls?: string | null;
  source_url_key?: string | null;
  provider?: string | null;
  original_provider?: string | null;
}

interface SimpleCoursesModalProps {
  visible: boolean;
  courses: Course[];
  matchCount: number;
  onClose: () => void;
  conversationId?: string | null;
  topic?: string | null;
  subTopic?: string | null;
}

type ModalStep = 'preview' | 'lead_form' | 'more_info_email' | 'success';

const CARD_WIDTH = 296;
const CARD_GAP = 14;
const CARD_INTERVAL = CARD_WIDTH + CARD_GAP;
const CAROUSEL_SIDE_PADDING = 18;
const CARD_PEEK = 28;

function normalizeILPhone(raw: string): string {
  let p = (raw ?? '').toString().trim().replace(/[^\d]/g, '');
  if (p.startsWith('972') && p.length === 12) p = '0' + p.slice(3);
  if (p.startsWith('0') && p.length !== 10) return '';
  return p;
}

function isValidPhone(raw: string): boolean {
  const p = normalizeILPhone(raw);
  return /^05\d{8}$/.test(p);
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((value || '').trim());
}

function cleanText(value?: string | null) {
  return (value || '')
    .toString()
    .replace(/\s+/g, ' ')
    .replace(/^[,.\-•\s]+/, '')
    .trim();
}

function cleanTopicText(topic?: string | null, subTopic?: string | null) {
  const t = cleanText(topic);
  const s = cleanText(subTopic);
  if (t && s) return `${t} / ${s}`;
  return t || s || 'התחום שביקשת';
}

function getCardTitle(course: CourseExtended, index: number) {
  return (
    cleanText(course.marketing_title) ||
    cleanText(course.name || course.title || course.original_title) ||
    `התאמה ${index + 1}`
  );
}

function getCourseName(course: CourseExtended) {
  return cleanText(course.original_title) || cleanText(course.title) || cleanText(course.name) || '';
}

function getCourseProvider(course: CourseExtended) {
  return cleanText(course.original_provider) || cleanText(course.provider) || '';
}

function getRank(course: CourseExtended, fallbackIndex: number) {
  const raw = course.relevant;
  const n = Number(raw);
  if (n >= 1 && n <= 3) return n;
  return fallbackIndex + 1;
}

function getFitLine(course: CourseExtended, topicLabel: string) {
  const fitLine = cleanText(course.fit_line);
  if (fitLine) return fitLine;
  const audience = cleanText(course.audience);
  if (audience) return audience;
  return `מתאים במיוחד למי שרוצה להתקדם ב${topicLabel}`;
}

function getUniqueLine(course: CourseExtended) {
  const uniqueLine = cleanText(course.unique_line);
  if (uniqueLine) return uniqueLine;
  const uniqueness = cleanText(course.uniqueness);
  if (uniqueness) return uniqueness;
  const summary = cleanText(course.summary);
  if (summary) return summary;
  return 'מציע יתרון פרקטי ברור שמתאים למה שחיפשת.';
}

function getOutcomeLine(course: CourseExtended) {
  const outcomeLine = cleanText(course.outcome_line);
  if (outcomeLine) return outcomeLine;
  const outcomes = cleanText(course.outcomes);
  if (outcomes) return outcomes;
  const deliverables = cleanText(course.deliverables);
  if (deliverables) return deliverables;
  return 'בסיום תהיה לך תוצאה מעשית שאפשר ליישם מיד.';
}

function getCourseUrl(course: CourseExtended): string | null {
  const raw = ((course as any).source_urls || '').toString().trim();
  if (!raw) return null;

  const cleaned = raw
    .replace(/[\[\]"']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const candidates = cleaned
    .split(/[\s,;]+/)
    .map((v: string) => v.trim())
    .filter(Boolean);

  const firstUrl =
    candidates.find((v: string) => /^https?:\/\//i.test(v)) ||
    candidates.find((v: string) => /^[\w.-]+\.[a-zA-Z]{2,}(\/|$)/.test(v)) ||
    null;

  if (!firstUrl) return null;

  if (/^https?:\/\//i.test(firstUrl)) return firstUrl;
  if (/^[\w.-]+\.[a-zA-Z]{2,}(\/|$)/.test(firstUrl)) return `https://${firstUrl}`;

  return null;
}

function getLeadGateCtaLabel() {
  return LEAD_GATE_COPY.button;
}

function logModalEvent(eventName: string, payload?: Record<string, unknown>) {
  try {
    console.log(`[SimpleCoursesModal] ${eventName}`, payload || {});
  } catch {}
}

function CourseCard({
  course,
  index,
  topicLabel,
  onMoreInfo,
}: {
  course: CourseExtended;
  index: number;
  topicLabel: string;
  onMoreInfo: () => void;
}) {
  const providerName = getCourseProvider(course);

  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle} numberOfLines={3}>
        {getCardTitle(course, index)}
      </Text>

      <Text style={styles.cardCourseName}>{getCourseName(course)}</Text>

      {!!providerName && <Text style={styles.cardProvider}>{providerName}</Text>}

      <View style={styles.cardLineBlock}>
        <Text style={styles.cardLineLabel}>למי זה מתאים</Text>
        <Text style={styles.cardLineText} numberOfLines={3}>
          {getFitLine(course, topicLabel)}
        </Text>
      </View>

      <View style={styles.cardLineBlock}>
        <Text style={styles.cardLineLabel}>מה מיוחד בקורס</Text>
        <Text style={styles.cardLineText} numberOfLines={3}>
          {getUniqueLine(course)}
        </Text>
      </View>

      <View style={styles.cardLineBlock}>
        <Text style={styles.cardLineLabel}>מה מקבלים בסוף</Text>
        <Text style={styles.cardLineText} numberOfLines={3}>
          {getOutcomeLine(course)}
        </Text>
      </View>

      <Text style={styles.cardHookText} numberOfLines={2}>
        {getLeadGateCtaLabel()}
      </Text>

      <Pressable
        style={({ pressed }) => [styles.cardSecondaryButton, pressed && { opacity: 0.86 }]}
        onPress={onMoreInfo}
        accessibilityRole="button"
        accessibilityLabel="השאירו פרטים"
      >
        <Text style={styles.cardSecondaryButtonText}>{LEAD_GATE_COPY.button}</Text>
      </Pressable>
    </View>
  );
}

export function SimpleCoursesModal({
  visible,
  courses,
  matchCount,
  onClose,
  conversationId = null,
  topic = null,
  subTopic = null,
}: SimpleCoursesModalProps) {
  const router = useRouter();

  const extendedCourses = useMemo(() => {
    const source = (courses as CourseExtended[]).slice(0, 3);
    return [...source].sort((a, b) => getRank(a, 99) - getRank(b, 99));
  }, [courses]);

  const fadeAnim = useRef(new Animated.Value(0)).current;

  const [step, setStep] = useState<ModalStep>('preview');
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [selectedCourseIndex, setSelectedCourseIndex] = useState<number | null>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMode, setSuccessMode] = useState<'lead' | 'more_info' | null>(null);

  const topicLabel = useMemo(() => cleanTopicText(topic, subTopic), [topic, subTopic]);
  const displayCount = matchCount || extendedCourses.length;
  const cleanPhone = useMemo(() => normalizeILPhone(phone), [phone]);

  const selectedCourse = useMemo(() => {
    if (selectedCourseIndex === null) return null;
    return extendedCourses[selectedCourseIndex] || null;
  }, [extendedCourses, selectedCourseIndex]);

  useEffect(() => {
    if (!visible) {
      setStep('preview');
      setActiveCardIndex(0);
      setSelectedCourseIndex(null);
      setFullName('');
      setPhone('');
      setPhoneError(null);
      setNameError(null);
      setEmail('');
      setEmailError(null);
      setSuccessMode(null);
      setIsSubmitting(false);
      setSubmitError(null);
      fadeAnim.setValue(0);
      return;
    }
    Animated.timing(fadeAnim, { toValue: 1, duration: 220, useNativeDriver: true }).start();
  }, [visible, fadeAnim]);

  useEffect(() => {
    if (visible && extendedCourses.length > 0) {
      (async () => {
        try {
          const userId = await getCurrentUserId();
          const sessionId = getOrCreateSessionId();
          await trackPopupView({
            conversationId,
            userId,
            sessionId,
            coursesCount: extendedCourses.length,
            topic,
            subTopic,
          });
          logModalEvent('popup_view', {
            conversationId,
            coursesCount: extendedCourses.length,
            topic,
            subTopic,
          });
        } catch {}
      })();
    }
  }, [visible, extendedCourses.length, conversationId, topic, subTopic]);

  const handleCardScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const x = e.nativeEvent.contentOffset.x;
      const idx = Math.round(x / CARD_INTERVAL);
      setActiveCardIndex(Math.min(Math.max(idx, 0), extendedCourses.length - 1));
    },
    [extendedCourses.length]
  );

  const handlePhoneChange = useCallback(
    (t: string) => {
      setPhone(t);
      if (phoneError) setPhoneError(isValidPhone(t) ? null : 'מספר טלפון לא תקין');
    },
    [phoneError]
  );

  const handlePhoneBlur = useCallback(() => {
    if (phone.trim()) setPhoneError(isValidPhone(phone) ? null : 'מספר טלפון לא תקין');
  }, [phone]);

  const handleNameChange = useCallback(
    (t: string) => {
      setFullName(t);
      if (nameError && t.trim().length >= 2) setNameError(null);
    },
    [nameError]
  );

  const handleEmailChange = useCallback(
    (t: string) => {
      setEmail(t);
      if (emailError) setEmailError(isValidEmail(t) ? null : 'נא להזין כתובת מייל תקינה');
    },
    [emailError]
  );

  const validateLeadForm = () => {
    let valid = true;

    if (!fullName.trim() || fullName.trim().length < 2) {
      setNameError('נא להזין שם מלא');
      valid = false;
    }

    if (!isValidPhone(phone)) {
      setPhoneError('מספר טלפון לא תקין');
      valid = false;
    }

    if (!selectedCourse) {
      setSubmitError('לא נבחרה התאמה. חזור ובחר את אחת האפשרויות.');
      valid = false;
    }

    return valid;
  };

  const handleGeneralCTA = () => {
    const indexToUse = activeCardIndex >= 0 ? activeCardIndex : 0;
    setSelectedCourseIndex(indexToUse);
    setSubmitError(null);
    setStep('lead_form');
    logModalEvent('discount_cta_click', {
      selectedCourseIndex: indexToUse,
      selectedCourseTitle: getCardTitle(extendedCourses[indexToUse], indexToUse),
      topic,
      subTopic,
      conversationId,
    });
  };

  const openMoreInfoForCourse = (index: number) => {
    setSelectedCourseIndex(index);
    setEmail('');
    setEmailError(null);
    setSubmitError(null);
    setStep('more_info_email');
    logModalEvent('more_info_click', {
      selectedCourseIndex: index,
      selectedCourseTitle: getCardTitle(extendedCourses[index], index),
      topic,
      subTopic,
      conversationId,
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validateLeadForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const pickedCourse = selectedCourse as any;
      const selectedCourseTitle = selectedCourse
        ? getCardTitle(selectedCourse, selectedCourseIndex || 0)
        : '';

      try {
        await supabase.from('leads').insert({
          full_name: fullName.trim(),
          phone: cleanPhone,
          course_id: pickedCourse?.id ?? pickedCourse?.course_id ?? 'unknown',
          source: 'inline_courses_modal_v14',
          status: 'new',
          notes: JSON.stringify({
            topic,
            subTopic,
            conversationId,
            selectedCourseIndex,
            selectedCourseTitle,
            flowType: 'lead_form',
            selectedCourses: extendedCourses.map((c: any, i) => ({
              id: c.id ?? c.course_id ?? `idx_${i}`,
              marketing_title: c.marketing_title || c.name || c.title || c.original_title || '',
              original_title: c.original_title || c.title || '',
              original_provider: c.original_provider || c.provider || '',
              price: c.price || '',
              duration: c.duration || '',
            })),
          }),
        });
      } catch (e) {
        console.error('[SimpleCoursesModal] leads error:', e);
      }

      logModalEvent('submit_success', {
        selectedCourseIndex,
        selectedCourseTitle,
        phone: cleanPhone,
      });

      setSuccessMode('lead');
      setStep('success');

      router.push({
        pathname: '/thank-you',
        params: {
          userSummary: topicLabel,
          selectedCourses: JSON.stringify(
            extendedCourses.map((c: any, i) => ({
              id: c.id ?? c.course_id ?? `idx_${i}`,
              marketing_title: c.marketing_title || c.name || c.title || c.original_title || '',
              original_title: c.original_title || c.title || '',
              original_provider: c.original_provider || c.provider || '',
            }))
          ),
          matchCount: String(displayCount),
          topic: topic ?? '',
          subtopic: subTopic ?? '',
        },
      });
    } catch (err) {
      console.error('[SimpleCoursesModal] submit error:', err);
      setSubmitError('אירעה שגיאה. נסה שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoreInfoSubmit = async () => {
    if (isSubmitting) return;

    if (!isValidEmail(email)) {
      setEmailError('נא להזין כתובת מייל תקינה');
      return;
    }

    if (!selectedCourse) {
      setSubmitError('לא נבחר קורס.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const pickedCourse = selectedCourse as any;
      const selectedCourseTitle = getCardTitle(selectedCourse, selectedCourseIndex || 0);
      const courseUrl = getCourseUrl(selectedCourse);

      try {
        await supabase.from('leads').insert({
          full_name: 'לא צוין',
          phone: 'לא צוין',
          email: email.trim(),
          course_id: pickedCourse?.id ?? pickedCourse?.course_id ?? 'unknown',
          source: 'inline_courses_modal_more_info_v1',
          status: 'new',
          notes: JSON.stringify({
            email: email.trim(),
            topic,
            subTopic,
            conversationId,
            selectedCourseIndex,
            selectedCourseTitle,
            originalTitle: getCourseName(selectedCourse),
            originalProvider: getCourseProvider(selectedCourse),
            courseUrl,
            flowType: 'more_info_email',
            sourceField: 'processed_raw_courses.source_urls',
          }),
        });
      } catch (e) {
        console.error('[SimpleCoursesModal] leads more_info error:', e);
      }

      logModalEvent('more_info_submit_success', {
        selectedCourseIndex,
        selectedCourseTitle,
        courseUrl,
      });

      if (!courseUrl) {
        setSubmitError('לא נמצא קישור בשדה source_urls של הקורס.');
        return;
      }

      setSuccessMode('more_info');
      setStep('success');

      if (Platform.OS === 'web') {
        const openedWindow = window.open(courseUrl, '_blank', 'noopener,noreferrer');
        if (!openedWindow) {
          window.location.href = courseUrl;
        }
      } else {
        const canOpen = await Linking.canOpenURL(courseUrl);
        if (canOpen) {
          await Linking.openURL(courseUrl);
        } else {
          setSubmitError('לא ניתן לפתוח את הקישור מהשדה source_urls כרגע. נסו שוב בעוד רגע.');
          setStep('more_info_email');
          return;
        }
      }
    } catch (err) {
      console.error('[SimpleCoursesModal] more_info submit error:', err);
      setSubmitError('אירעה שגיאה. נסה שוב.');
      setStep('more_info_email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    logModalEvent('modal_close', { step, activeCardIndex, selectedCourseIndex });
    onClose();
  };


  if (!visible) return null;

  return (
    <View style={[styles.overlay, { pointerEvents: 'auto' }]}>
      <Pressable style={StyleSheet.absoluteFillObject} onPress={handleClose} accessible={false} />

      <Animated.View
        style={[
          styles.sheet,
          {
            opacity: fadeAnim,
            transform: [
              {
                translateY: fadeAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [32, 0],
                }),
              },
            ],
          },
        ]}
        pointerEvents="auto"
      >
        <View style={[styles.header, step === 'more_info_email' && styles.headerMinimal]}>
          <Pressable
            onPress={handleClose}
            style={styles.closeButton}
            accessibilityRole="button"
            accessibilityLabel="סגור"
          >
            <X size={20} color="#94A3B8" />
          </Pressable>

          {step !== 'more_info_email' && (
            <View style={styles.headerTextWrap}>
              {step === 'preview' && (
                <>
                  <Text style={styles.title}>{LEAD_GATE_COPY.title}</Text>
                </>
              )}

              {step === 'lead_form' && (
                <>
                  <Text style={styles.title}>{LEAD_GATE_COPY.title}</Text>
                  <Text style={styles.subtitle}>
                    {LEAD_GATE_COPY.supportingText}
                  </Text>
                </>
              )}

              {step === 'success' && (
                <>
                  <Text style={styles.title}>{LEAD_GATE_COPY.successTitle}</Text>
                  <Text style={styles.subtitle}>{LEAD_GATE_COPY.successText}</Text>
                </>
              )}
            </View>
          )}
        </View>

        {step === 'preview' && (
          <>
            <ScrollView
              style={styles.previewBody}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingBottom: 8 }}
            >
              {extendedCourses.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>עדיין אין התאמות מוכנות להצגה</Text>
                  <Text style={styles.emptyText}>אפשר לחזור לשיחה ולחדד מה אתם מחפשים.</Text>
                </View>
              ) : (
                <>
                  <ScrollView
                    horizontal
                    pagingEnabled={false}
                    showsHorizontalScrollIndicator={false}
                    snapToInterval={CARD_INTERVAL}
                    decelerationRate="fast"
                    contentContainerStyle={styles.carouselContent}
                    onScroll={handleCardScroll}
                    scrollEventThrottle={16}
                    style={styles.carousel}
                  >
                    {extendedCourses.map((course, index) => (
                      <CourseCard
                        key={(course as any).id ?? (course as any).course_id ?? index}
                        course={course}
                        index={index}
                        topicLabel={topicLabel}
                        onMoreInfo={() => openMoreInfoForCourse(index)}
                      />
                    ))}
                  </ScrollView>

                  {extendedCourses.length > 1 && (
                    <View style={styles.dotsRow}>
                      {extendedCourses.map((_, i) => (
                        <View key={i} style={[styles.dot, i === activeCardIndex && styles.dotActive]} />
                      ))}
                    </View>
                  )}
                </>
              )}
            </ScrollView>

            {extendedCourses.length > 0 && (
              <View style={styles.footer}>
                <Pressable
                  style={({ pressed }) => [styles.primaryButton, pressed && styles.primaryButtonPressed]}
                  onPress={handleGeneralCTA}
                  accessibilityRole="button"
                >
                  <Text style={styles.primaryButtonText}>{getLeadGateCtaLabel()}</Text>
                </Pressable>
              </View>
            )}
          </>
        )}

        {step === 'lead_form' && (
          <>
            <ScrollView
              style={styles.formScroll}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.formScrollContent}
            >
              <View style={styles.teamMessageBox}>
                <Text style={styles.teamMessageText}>
                  {LEAD_GATE_COPY.supportingText}
                </Text>
              </View>

              <View style={styles.fieldBlock}>
                <Text style={styles.fieldLabel}>שם מלא</Text>
                <TextInput
                  style={[styles.input, nameError && styles.inputError]}
                  value={fullName}
                  onChangeText={handleNameChange}
                  placeholder="הזן שם מלא"
                  placeholderTextColor="#4B5563"
                  accessibilityLabel="שם מלא"
                  returnKeyType="next"
                />
                {nameError && <Text style={styles.fieldError}>{nameError}</Text>}
              </View>

              <View style={styles.fieldBlock}>
                <Text style={styles.fieldLabel}>טלפון</Text>
                <TextInput
                  style={[styles.input, phoneError && styles.inputError]}
                  value={phone}
                  onChangeText={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  placeholder="050-1234567"
                  placeholderTextColor="#4B5563"
                  keyboardType="phone-pad"
                  accessibilityLabel="טלפון"
                  returnKeyType="done"
                  onSubmitEditing={handleSubmit}
                />
                {phoneError && <Text style={styles.fieldError}>{phoneError}</Text>}
              </View>

              {submitError && (
                <View style={styles.submitErrorBox}>
                  <Text style={styles.submitErrorText}>{submitError}</Text>
                </View>
              )}

              <Text style={styles.consentNote}>
                בלחיצה על הכפתור אני מאשר/ת קבלת פניות מקורסי ומהמוסדות הרלוונטיים בנוגע לקורסים שהוצגו.
                ניתן לבטל בכל עת.{' '}
                <Text style={styles.consentLink} onPress={() => router.push('/legal/privacy')}>
                  מדיניות פרטיות
                </Text>
              </Text>
            </ScrollView>

            <View style={styles.footer}>
              <Pressable
                style={[styles.primaryButton, isSubmitting && styles.buttonDisabled]}
                onPress={handleSubmit}
                disabled={isSubmitting}
                accessibilityRole="button"
              >
                {isSubmitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.primaryButtonText}>{getLeadGateCtaLabel()}</Text>
                )}
              </Pressable>

              <Pressable
                style={({ pressed }) => [styles.backButton, pressed && { opacity: 0.7 }]}
                onPress={() => setStep('preview')}
                accessibilityRole="button"
              >
                <Text style={styles.backButtonText}>חזרה</Text>
              </Pressable>
            </View>
          </>
        )}

        {step === 'more_info_email' && (
          <>
            <ScrollView
              style={styles.formScroll}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.moreInfoScrollContent}
            >
              <View style={styles.fieldBlock}>
                <TextInput
                  style={[styles.input, emailError && styles.inputError]}
                  value={email}
                  onChangeText={handleEmailChange}
                  placeholder="כתובת מייל"
                  placeholderTextColor="#4B5563"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  accessibilityLabel="כתובת מייל"
                  returnKeyType="done"
                  onSubmitEditing={handleMoreInfoSubmit}
                />
                {emailError && <Text style={styles.fieldError}>{emailError}</Text>}
              </View>

              {submitError && (
                <View style={styles.submitErrorBox}>
                  <Text style={styles.submitErrorText}>{submitError}</Text>
                </View>
              )}

              <Text style={styles.consentNote}>
                בלחיצה על הכפתור אני מאשר/ת העברת פרטים לצורך יצירת קשר.{' '}
                <Text style={styles.consentLink} onPress={() => router.push('/legal/privacy')}>
                  מדיניות פרטיות
                </Text>
              </Text>
            </ScrollView>

            <View style={styles.footer}>
              <Pressable
                style={[styles.primaryButton, isSubmitting && styles.buttonDisabled]}
                onPress={handleMoreInfoSubmit}
                disabled={isSubmitting}
                accessibilityRole="button"
              >
                {isSubmitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.primaryButtonText}>מעבר לאתר הרשמי</Text>
                )}
              </Pressable>

              <Pressable
                style={({ pressed }) => [styles.backButton, pressed && { opacity: 0.7 }]}
                onPress={() => setStep('preview')}
                accessibilityRole="button"
              >
                <Text style={styles.backButtonText}>חזרה</Text>
              </Pressable>
            </View>
          </>
        )}

        {step === 'success' && (
          <>
            <View style={styles.successBody}>
              <View style={styles.successIconCircle}>
                <CheckCircle2 size={44} color="#22C55E" />
              </View>
              <Text style={styles.successTitle}>{LEAD_GATE_COPY.successTitle}</Text>
              <Text style={styles.successSubtitle}>{LEAD_GATE_COPY.successText}</Text>
            </View>

            <View style={styles.footer}>
              <Pressable
                style={({ pressed }) => [styles.primaryButton, pressed && styles.primaryButtonPressed]}
                onPress={handleClose}
                accessibilityRole="button"
              >
                <Text style={styles.primaryButtonText}>סגור</Text>
              </Pressable>
            </View>
          </>
        )}
      </Animated.View>
    </View>
  );
}

export default SimpleCoursesModal;

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999999,
    backgroundColor: 'rgba(2, 6, 23, 0.78)',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 1160,
    maxHeight: '90%',
    backgroundColor: '#080F1E',
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(139, 92, 246, 0.2)',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -8 },
    shadowOpacity: 0.35,
    shadowRadius: 22,
    elevation: 20,
  },
  header: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 12,
    paddingTop: 18,
    paddingHorizontal: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.06)',
    backgroundColor: '#0B1225',
  },
  headerMinimal: {
    justifyContent: 'flex-start',
    paddingBottom: 10,
    borderBottomWidth: 0,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    marginTop: 2,
  },
  headerTextWrap: { flex: 1, gap: 6 },
  title: {
    fontSize: 17,
    fontFamily: theme.fonts.bold,
    color: '#F1F5F9',
    textAlign: 'right',
    lineHeight: 26,
  },
  subtitle: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: '#94A3B8',
    textAlign: 'right',
    lineHeight: 21,
  },

  previewBody: {
    flex: 1,
    paddingTop: 18,
    paddingBottom: 4,
    overflow: 'visible',
  },
  carousel: {
    flexGrow: 0,
    overflow: 'visible',
  },
  carouselContent: {
    paddingRight: CAROUSEL_SIDE_PADDING,
    paddingLeft: CAROUSEL_SIDE_PADDING - CARD_PEEK,
    paddingBottom: 12,
    gap: CARD_GAP,
    flexDirection: 'row',
  },
  dotsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 7,
    marginTop: 14,
    marginBottom: 4,
  },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.18)' },
  dotActive: { width: 18, backgroundColor: '#A855F7', borderRadius: 3 },

  card: {
    width: CARD_WIDTH,
    borderRadius: 24,
    backgroundColor: '#0B1730',
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.22)',
    padding: 18,
    flexShrink: 0,
    shadowColor: '#020617',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.3,
    shadowRadius: 14,
    elevation: 7,
  },
  cardTitle: {
    fontSize: 19,
    fontFamily: theme.fonts.bold,
    color: '#FFFFFF',
    textAlign: 'right',
    lineHeight: 29,
    marginBottom: 6,
  },
  cardCourseName: {
    fontSize: 13,
    fontFamily: theme.fonts.medium,
    color: '#C4B5FD',
    textAlign: 'right',
    lineHeight: 20,
    marginBottom: 6,
  },
  cardProvider: {
    fontSize: 12,
    fontFamily: theme.fonts.medium,
    color: 'rgba(176, 38, 255, 0.75)',
    textAlign: 'right',
    marginBottom: 10,
    marginTop: 0,
  },
  cardLineBlock: {
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: 1,
    borderColor: 'rgba(148,163,184,0.08)',
    paddingVertical: 10,
    paddingHorizontal: 11,
    marginBottom: 8,
  },
  cardLineLabel: {
    fontSize: 11,
    fontFamily: theme.fonts.bold,
    color: '#A78BFA',
    textAlign: 'right',
    marginBottom: 4,
  },
  cardLineText: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: '#E2E8F0',
    textAlign: 'right',
    lineHeight: 20,
  },
  cardHookText: {
    fontSize: 12,
    fontFamily: theme.fonts.medium,
    color: '#C4B5FD',
    textAlign: 'right',
    lineHeight: 19,
    marginTop: 4,
    marginBottom: 10,
  },
  cardSecondaryButton: {
    alignSelf: 'flex-end',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.45)',
    backgroundColor: 'rgba(124,58,237,0.14)',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  cardSecondaryButtonText: {
    fontSize: 12,
    fontFamily: theme.fonts.medium,
    color: '#E9D5FF',
    textAlign: 'right',
  },

  footer: {
    padding: 16,
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.06)',
    backgroundColor: '#0B1225',
  },
  primaryButton: {
    backgroundColor: '#8B5CF6',
    borderRadius: 16,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#8B5CF6',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 6,
  },
  primaryButtonPressed: { transform: [{ scale: 0.992 }], opacity: 0.94 },
  primaryButtonText: {
    fontSize: 15,
    fontFamily: theme.fonts.bold,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  buttonDisabled: { opacity: 0.55 },
  backButton: { alignItems: 'center', paddingVertical: 8 },
  backButtonText: {
    fontSize: 13,
    fontFamily: theme.fonts.medium,
    color: '#64748B',
    textAlign: 'center',
  },

  formScroll: { flex: 1, paddingHorizontal: 18, paddingTop: 16 },
  formScrollContent: { paddingBottom: 24 },
  moreInfoScrollContent: {
    paddingBottom: 24,
    paddingTop: 2,
  },

  teamMessageBox: {
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.3)',
    borderRadius: 18,
    paddingVertical: 16,
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  teamMessageText: {
    fontSize: 15,
    fontFamily: theme.fonts.bold,
    color: '#E9D5FF',
    textAlign: 'right',
    lineHeight: 24,
  },

  fieldBlock: { marginBottom: 14 },
  fieldLabel: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: '#E2E8F0',
    textAlign: 'right',
    marginBottom: 7,
  },
  input: {
    backgroundColor: '#0F172A',
    borderWidth: 1,
    borderColor: '#1E293B',
    borderRadius: 14,
    paddingVertical: 13,
    paddingHorizontal: 14,
    color: '#F8FAFC',
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    textAlign: 'right',
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  inputError: { borderColor: '#EF4444' },
  fieldError: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 5,
  },
  submitErrorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.3)',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  submitErrorText: {
    fontSize: 13,
    fontFamily: theme.fonts.medium,
    color: '#FCA5A5',
    textAlign: 'right',
  },
  consentNote: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: '#475569',
    textAlign: 'right',
    lineHeight: 19,
    marginTop: 6,
    marginBottom: 16,
  },
  consentLink: {
    fontSize: 12,
    fontFamily: theme.fonts.medium,
    color: '#A78BFA',
    textDecorationLine: 'underline',
  },

  successBody: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 30,
    gap: 4,
  },
  successIconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(34, 197, 94, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(34, 197, 94, 0.24)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  successTitle: {
    fontSize: 22,
    fontFamily: theme.fonts.bold,
    color: '#F8FAFC',
    textAlign: 'center',
    marginBottom: 6,
  },
  successSubtitle: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: '#94A3B8',
    textAlign: 'center',
    marginBottom: 16,
  },
  successStep: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  successStepDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#22C55E',
    flexShrink: 0,
  },
  successStepText: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: '#CBD5E1',
    textAlign: 'right',
    lineHeight: 22,
    flex: 1,
  },

  emptyState: {
    marginHorizontal: 18,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(148,163,184,0.14)',
    backgroundColor: 'rgba(15,23,42,0.55)',
    paddingVertical: 18,
    paddingHorizontal: 16,
  },
  emptyTitle: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#E2E8F0',
    textAlign: 'right',
    marginBottom: 6,
  },
  emptyText: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: '#94A3B8',
    textAlign: 'right',
    lineHeight: 21,
  },
});